<template>
  <div>
    <Menu as="div" class="relative inline-block text-left">
      <div>
        <MenuButton
          v-slot="{ open }"
          data-test="main-toolbar-views"
          class="bg-bluedark-50 text-bluedark-700 hover:bg-bluedark-100 disabled:bg-bluedark-25 disabled:text-bluedark-300 inline-flex w-full justify-center gap-x-1.5 rounded-md px-3 py-2 text-sm font-semibold ring-inset focus:border-transparent focus:ring-0"
        >
          <component :is="currentViewIcon?.icon" class="text-bluedark-500 h-5 w-5" />
          <ChevronDownIcon
            class="text-bluedark-500 -mr-1 size-5 transition-all"
            :class="{
              'rotate-180': open
            }"
            aria-hidden="true"
          />
        </MenuButton>
      </div>

      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
      >
        <MenuItems
          class="absolute top-full left-0 z-10 mt-2 flex w-56 origin-top-left flex-col gap-2 rounded-md bg-white p-2 shadow-lg ring-1 ring-black/5 focus:outline-hidden"
        >
          <MenuItem v-for="view in views" :key="view.name" v-slot="{ close }">
            <template v-if="!view.handler">
              <div>
                <TooltipBaseLink
                  v-slot="{ currentPath }"
                  :path="view.url"
                  :disabled="disabled"
                  :data-test="view.dataTest"
                  @click="close"
                >
                  <component
                    :is="view.icon"
                    class="h-5 w-5"
                    :class="{ 'text-bluedark-600': currentPath, 'text-gray-700': !currentPath }"
                  />
                  <span
                    class="grow text-sm"
                    :class="{ 'text-bluedark-600': currentPath, 'text-gray-700': !currentPath }"
                  >
                    {{ view.label }}
                  </span>
                  <SelectionIcon v-if="currentPath" class="text-bluedark-600 h-5 w-5" />
                </TooltipBaseLink>
              </div>
            </template>
            <template v-else>
              <button
                :disabled="disabled"
                class="hover:bg-bluedark-50 hover:text-bluedark-700 flex items-center gap-2 rounded-sm px-[14px] py-2 text-sm font-medium transition-colors disabled:text-gray-300"
                :class="{
                  'text-bluedark-600 hover:bg-bluedark-100 bg-gray-50': currentView === view.name,
                  'text-gray-700': currentView !== view.name
                }"
                :data-test="view.dataTest"
                @click="view.handler"
              >
                <component :is="view.icon" class="h-5 w-5" />
                <span class="grow text-start text-sm">{{ view.tooltipText }}</span>
                <SelectionIcon v-if="currentView === view.name" class="text-bluedark-600 h-5 w-5" />
              </button>
            </template>
          </MenuItem>
        </MenuItems>
      </transition>
    </Menu>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import useNavigation from '@/hooks/use-navigation'
import ChevronDownIcon from '@/assets/untitled-ui/chevron-down.svg'
import TooltipBaseLink from '@/components/TooltipBaseLink.vue'
import { useRoute } from 'vue-router'
import SelectionIcon from '@/assets/untitled-ui/check-circle.svg'

const route = useRoute()

const props = defineProps({
  views: {
    type: Array,
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  currentView: {
    type: String,
    required: false,
    default: ''
  },
  isPlanToolbar: {
    type: Boolean,
    required: false,
    default: false
  }
})

const { navigationConfig } = useNavigation()

const isCurrentPath = (path) => route.fullPath.match(/^\/([^?]+)/)?.[0] === path

const currentViewIcon = computed(() => {
  return props.views.find((view) => {
    return (
      props.currentView === view.name || isCurrentPath(navigationConfig.value[view.name]?.url || '')
    )
  })
})
</script>
